import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, Companion } from '../store/gameStore';
import { useOptimizedCalculations } from '../store/optimizedGameStore';

interface ActiveCompanionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ActiveCompanionsModal: React.FC<ActiveCompanionsModalProps> = ({ 
  isOpen, 
  onClose 
}) => {
  const { companions, getMaxCompanionSlots, formatNumber, getCompanionBonus } = useGameStore();
  const { setActiveCompanionOptimized, removeActiveCompanionOptimized, removeAllActiveCompanionsOptimized } = useOptimizedCalculations();

  const getRarityColor = (type: Companion['type']) => {
    switch (type) {
      case 'legendary': return 'from-yellow-400 to-orange-500';
      case 'epic': return 'from-purple-400 to-pink-500';
      case 'rare': return 'from-blue-400 to-cyan-500';
      case 'common': return 'from-gray-400 to-gray-600';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  const getRarityBorder = (type: Companion['type']) => {
    switch (type) {
      case 'legendary': return 'border-yellow-400';
      case 'epic': return 'border-purple-400';
      case 'rare': return 'border-blue-400';
      case 'common': return 'border-gray-400';
      default: return 'border-gray-400';
    }
  };

  const getRarityName = (type: Companion['type']) => {
    switch (type) {
      case 'legendary': return 'Legendär';
      case 'epic': return 'Episch';
      case 'rare': return 'Selten';
      case 'common': return 'Gewöhnlich';
      default: return 'Unbekannt';
    }
  };

  const handleSlotClick = (slotIndex: number, companion?: Companion) => {
    if (companion) {
      // Entferne Begleiter aus dem Slot
      removeActiveCompanionOptimized(slotIndex);
    }
  };

  const handleDeactivateAll = () => {
    // Entferne alle aktiven Begleiter auf einmal
    removeAllActiveCompanionsOptimized();
  };

  const getAbilityIcon = (type: string) => {
    switch (type) {
      case 'clickMultiplier': return '👆';
      case 'coinPerSecond': return '💰';
      case 'gemChance': return '💎';
      case 'xpBonus': return '⭐';
      case 'gardenBonus': return '🌺';
      case 'autoClickPower': return '🤖';
      case 'upgradeDiscount': return '🏷️';
      case 'prestigeBonus': return '🌟';
      case 'criticalHit': return '💥';
      case 'healing': return '💚';
      case 'shield': return '🛡️';
      case 'counterAttack': return '🔄';
      case 'lifeSteal': return '🩸';
      case 'armorPenetration': return '🗡️';
      default: return '✨';
    }
  };

  const getAbilityLabel = (type: string) => {
    switch (type) {
      case 'clickMultiplier': return 'Klick-Multiplikator';
      case 'coinPerSecond': return 'Münzen pro Sekunde';
      case 'gemChance': return 'Edelstein-Chance';
      case 'xpBonus': return 'Erfahrungs-Bonus';
      case 'gardenBonus': return 'Garten-Bonus';
      case 'autoClickPower': return 'Auto-Klick-Kraft';
      case 'upgradeDiscount': return 'Upgrade-Rabatt';
      case 'prestigeBonus': return 'Prestige-Bonus';
      case 'criticalHit': return 'Kritische Treffer';
      case 'healing': return 'Heilung';
      case 'shield': return 'Schild';
      case 'counterAttack': return 'Gegenangriff';
      case 'lifeSteal': return 'Lebensentzug';
      case 'armorPenetration': return 'Rüstungsdurchdringung';
      default: return type;
    }
  };

  const getAbilitySuffix = (type: string) => {
    switch (type) {
      case 'clickMultiplier':
      case 'gemChance':
      case 'xpBonus':
      case 'gardenBonus':
      case 'upgradeDiscount':
      case 'prestigeBonus':
      case 'lifeSteal':
        return '%';
      default:
        return '';
    }
  };



  // Funktion zum Zusammenfassen aller aktiven Begleiter-Stats
  const getCombinedActiveCompanionStats = () => {
    const activeCompanions = companions.active.filter(c => c);
    const combinedStats = new Map<string, number>();

    activeCompanions.forEach(companion => {
      companion.abilities.forEach(ability => {
        const key = ability.type;
        const currentValue = combinedStats.get(key) || 0;
        combinedStats.set(key, currentValue + ability.value);
      });
    });

    return Array.from(combinedStats.entries()).map(([type, value]) => ({
      type,
      value,
      icon: getAbilityIcon(type),
      label: getAbilityLabel(type),
      suffix: getAbilitySuffix(type)
    }));
  };

  // Funktion zum Zusammenfassen der Fähigkeiten eines einzelnen Begleiters
  const getCombinedCompanionAbilities = (companion: Companion) => {
    const combinedAbilities = new Map<string, number>();

    companion.abilities.forEach(ability => {
      const key = ability.type;
      const currentValue = combinedAbilities.get(key) || 0;
      combinedAbilities.set(key, currentValue + ability.value);
    });

    return Array.from(combinedAbilities.entries()).map(([type, value]) => ({
      type,
      value,
      icon: getAbilityIcon(type),
      label: getAbilityLabel(type),
      suffix: getAbilitySuffix(type)
    }));
  };

  return (
    <AnimatePresence key="active-companions-modal">
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 modal-container"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 sm:p-6 rounded-t-xl z-10">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="text-3xl sm:text-4xl">⭐</div>
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold">Aktive Begleiter</h1>
                    <div className="text-sm opacity-90">
                      {companions.active.filter(c => c).length}/{getMaxCompanionSlots()} Slots belegt
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Beste Begleiter hinzufügen Button */}
                  <motion.button
                    onClick={() => {
                      const availableCompanions = companions.discovered.filter(companion => !companions.active.some(activeCompanion => activeCompanion?.id === companion.id));
                      const maxSlots = getMaxCompanionSlots();
                      const currentActiveCount = companions.active.filter(c => c).length;
                      const availableSlots = maxSlots - currentActiveCount;
                      
                      // Bewerte Begleiter basierend auf ihren Fähigkeiten
                      const scoredCompanions = availableCompanions.map(companion => {
                        let score = 0;
                        
                        // Bewerte jede Fähigkeit
                        companion.abilities.forEach(ability => {
                          switch (ability.type) {
                            case 'clickMultiplier':
                              score += ability.value * 10; // Hohe Gewichtung für Klick-Bonus
                              break;
                            case 'coinPerSecond':
                              score += ability.value * 5; // Mittlere Gewichtung für Münzen
                              break;
                            case 'xpBonus':
                              score += ability.value * 8; // Hohe Gewichtung für XP
                              break;
                            case 'gemChance':
                              score += ability.value * 6; // Mittlere Gewichtung für Edelsteine
                              break;
                            case 'gardenBonus':
                              score += ability.value * 4; // Niedrige Gewichtung für Garten
                              break;
                            case 'autoClickPower':
                              score += ability.value * 3; // Niedrige Gewichtung für Auto-Klick
                              break;
                            case 'upgradeDiscount':
                              score += ability.value * 7; // Hohe Gewichtung für Rabatte
                              break;
                            case 'prestigeBonus':
                              score += ability.value * 9; // Sehr hohe Gewichtung für Prestige
                              break;
                            case 'criticalHit':
                              score += ability.value * 2; // Kampf-Fähigkeiten niedriger gewichtet
                              break;
                            case 'healing':
                              score += ability.value * 1;
                              break;
                            case 'shield':
                              score += ability.value * 1;
                              break;
                            case 'counterAttack':
                              score += ability.value * 1;
                              break;
                            case 'lifeSteal':
                              score += ability.value * 1;
                              break;
                            case 'armorPenetration':
                              score += ability.value * 1;
                              break;
                            default:
                              score += ability.value;
                          }
                        });
                        
                        // Bonus für Seltenheit
                        switch (companion.type) {
                          case 'legendary':
                            score += 1000;
                            break;
                          case 'epic':
                            score += 500;
                            break;
                          case 'rare':
                            score += 200;
                            break;
                          case 'common':
                            score += 50;
                            break;
                        }
                        
                        // Bonus für höhere Level
                        score += (companion.level || 1) * 10;
                        
                        return { companion, score };
                      });
                      
                      // Sortiere nach Score (beste zuerst) und wähle die besten aus
                      const bestCompanions = scoredCompanions
                        .sort((a, b) => b.score - a.score)
                        .slice(0, availableSlots)
                        .map(item => item.companion);
                      
                      // Füge die besten Begleiter hinzu
                      bestCompanions.forEach(companion => {
                        setActiveCompanionOptimized(companion);
                      });
                    }}
                    disabled={companions.discovered.filter(companion => !companions.active.some(activeCompanion => activeCompanion?.id === companion.id)).length === 0 || companions.active.filter(c => c).length >= getMaxCompanionSlots()}
                    className="bg-green-500 hover:bg-green-600 disabled:bg-gray-500 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>⭐</span>
                    <span className="hidden sm:inline">Beste hinzufügen</span>
                  </motion.button>
                  
                  {/* Alle deaktivieren Button - nur anzeigen wenn Begleiter aktiv sind */}
                  {companions.active.filter(c => c).length > 0 && (
                    <motion.button
                      onClick={handleDeactivateAll}
                      className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span>🗑️</span>
                      <span className="hidden sm:inline">Alle entfernen</span>
                    </motion.button>
                  )}
                  <button
                    onClick={onClose}
                    className="text-white hover:text-gray-200 transition-colors duration-200 text-2xl sm:text-3xl font-bold min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6 space-y-6">
              {/* Zusammengefasste aktive Begleiter-Stats - Kompakt */}
              {companions.active.filter(c => c).length > 0 && (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-3 border border-green-200">
                  <h3 className="text-base font-bold text-green-800 mb-3 flex items-center gap-2">
                    ⭐ Aktive Begleiter Stats
                  </h3>
                  <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1">
                    {getCombinedActiveCompanionStats().map((stat, index) => (
                      <div key={index} className="bg-white rounded-md p-1 text-center border border-green-200 shadow-sm hover:shadow-md transition-shadow">
                        <div className="text-xs mb-0.5">{stat.icon}</div>
                        <div className="text-[10px] font-semibold text-green-700 mb-0.5 truncate leading-tight">
                          {stat.label}
                        </div>
                        <div className="text-[10px] font-bold text-green-600">
                          +{formatNumber(stat.value)}{stat.suffix}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Begleiter-Slots */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Aktive Begleiter anzeigen */}
                {Array.from({ length: getMaxCompanionSlots() }, (_, index) => index)
                  .filter(slotIndex => companions.active[slotIndex]) // Nur Slots mit Begleitern
                  .map((slotIndex) => {
                    const companion = companions.active[slotIndex];
                    
                    return (
                      <motion.div
                        key={slotIndex}
                        className="relative overflow-hidden rounded-2xl cursor-pointer min-h-[240px] max-w-full group z-0 ring-4 ring-green-400 ring-opacity-80 shadow-2xl transition-all duration-300 ease-out"
                        whileHover={{ 
                          scale: 1.03,
                          y: -5,
                          transition: { duration: 0.2 }
                        }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => handleSlotClick(slotIndex, companion)}
                      >
                        {/* Hintergrund-Gradient */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${getRarityColor(companion.type)} opacity-90`} />
                        
                        {/* Glanz-Effekt */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        
                        {/* Slot-Nummer */}
                        <div className="absolute top-3 left-3 bg-green-500 text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-lg z-10">
                          Slot {slotIndex + 1}
                        </div>

                        {/* Hauptinhalt */}
                        <div className="relative z-10 p-4 h-full flex flex-col">
                          {/* Begleiter-Icon mit Glow-Effekt */}
                          <div className="text-center mb-3">
                            <div className="text-4xl sm:text-5xl mb-1 drop-shadow-lg animate-bounce">
                              {companion.icon.startsWith('data:') ? (
                                <img 
                                  src={companion.icon} 
                                  alt={companion.name}
                                  className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                                  style={{ display: 'inline-block' }}
                                />
                              ) : (
                                companion.icon
                              )}
                            </div>
                          </div>
                          
                          {/* Begleiter-Name und Info */}
                          <div className="text-center mb-3 flex-1">
                            <div className="text-white font-bold text-base sm:text-lg break-words mb-1 drop-shadow-lg">
                              {companion.name}
                            </div>
                            <div className="text-white/90 text-xs font-medium mb-2">
                              {getRarityName(companion.type)}
                            </div>
                            
                            {/* Level-Anzeige mit modernem Design */}
                            <div className="flex justify-center items-center gap-2 mb-2">
                              <div className="bg-white/20 backdrop-blur-sm rounded-full px-2 py-0.5 text-white/90 text-xs font-medium">
                                Level {companion.level || 1}
                              </div>
                              {companion.battleLevel && companion.battleLevel > 1 && (
                                <div className="bg-yellow-500/80 backdrop-blur-sm rounded-full px-2 py-0.5 text-white text-xs font-bold">
                                  ⚔️ Battle Lv.{companion.battleLevel}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Fähigkeiten in Raster - Zusammengefasst anzeigen */}
                          <div className="mb-3">
                            <div className="grid grid-cols-2 gap-1">
                              {getCombinedCompanionAbilities(companion).map((ability, idx) => {
                                return (
                                  <div key={idx} className="bg-white/15 backdrop-blur-sm rounded-lg px-2 py-1 text-xs break-words border border-white/20 hover:bg-white/25 transition-colors">
                                    <div className="text-center">
                                      <div className="text-lg mb-1">{ability.icon}</div>
                                      <div className="text-white/90 font-medium text-xs leading-tight mb-1 truncate">
                                        {ability.label}
                                      </div>
                                      <div className="text-white font-bold text-xs">
                                        +{formatNumber(ability.value)}{ability.suffix}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Footer mit Entfernen-Hinweis */}
                          <div className="text-center">
                            <div className="bg-red-500/90 backdrop-blur-sm rounded-lg px-2 py-1 text-white text-xs font-bold">
                              🗑️ Klicken zum Entfernen
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}

                {/* Leere Slots - Einfache Anzeige */}
                {(() => {
                  const emptySlots = Array.from({ length: getMaxCompanionSlots() }, (_, index) => index)
                    .filter(slotIndex => !companions.active[slotIndex]);
                  
                  if (emptySlots.length === 0) return null;
                  
                  return (
                    <div className="col-span-full">
                      <div className="bg-gray-100 border-2 border-gray-300 border-dashed rounded-xl p-4 text-center">
                        <div className="text-gray-600 font-medium">
                          {emptySlots.length} leere Slot{emptySlots.length > 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>





              {/* Hinweis wenn keine aktiven Begleiter */}
              {companions.active.filter(c => c).length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <div className="text-4xl mb-4">⭐</div>
                  <p className="text-lg mb-2">Keine aktiven Begleiter</p>
                  <p className="text-sm opacity-60">Klicke auf einen Begleiter in der Sammlung, um ihn zu aktivieren!</p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ActiveCompanionsModal; 